import {
  canAccessModule,
  getStoredAllowedModules,
  getStoredPermissions,
  hasAnyPermission,
  hasPermissionPrefix,
  type ModuleKey,
} from "@/lib/module-access";

export interface ConnectAccessContext {
  allowedModules?: ModuleKey[];
  permissions?: string[];
}

function contextModules(context?: ConnectAccessContext): ModuleKey[] {
  return context?.allowedModules ?? getStoredAllowedModules();
}

function contextPermissions(context?: ConnectAccessContext): string[] {
  return context?.permissions ?? getStoredPermissions();
}

function canOpenConnect(context?: ConnectAccessContext): boolean {
  const modules = contextModules(context);
  const permissions = contextPermissions(context);

  return (
    canAccessModule("connect", modules) &&
    hasPermissionPrefix(permissions, ["connect."])
  );
}

export function canManageConnect(
  permissions: string[] | null | undefined,
): boolean {
  return hasAnyPermission(permissions, [
    "connect.meetings.update",
    "connect.vehicles.update",
    "connect.visitors.update",
    "connect.matta.update",
  ]);
}

/**
 * Pure permission-based path access mapping.
 * No hardcoded role names — all access is controlled via RBAC UI permissions.
 * super_admin bypass is handled automatically by hasAnyPermission().
 */
function requiredPermissionsForPath(pathname: string): readonly string[] {
  if (pathname.startsWith("/connect/matta")) {
    return ["connect.matta.view", "connect.matta.update"];
  }

  if (
    pathname.startsWith("/connect/ga-assets") ||
    pathname.startsWith("/connect/receptionist-checklist")
  ) {
    return ["connect.ga_assets.view"];
  }

  if (pathname.startsWith("/connect/absensi-tlh-gps")) {
    return ["connect.matta.view"];
  }

  if (pathname.startsWith("/connect/vehicles")) {
    return ["connect.vehicles.view"];
  }

  if (pathname.startsWith("/connect/meetings")) {
    return ["connect.meetings.view", "connect.meetings.book"];
  }

  if (pathname.startsWith("/connect/visitors")) {
    return ["connect.visitors.view", "connect.visitors.create"];
  }

  if (pathname.startsWith("/connect/reports")) {
    return ["connect.reports.view"];
  }

  if (pathname.startsWith("/connect/notifications")) {
    return ["connect.notifications.view"];
  }

  if (pathname.startsWith("/connect/activity-logs")) {
    return ["connect.activity_logs.view"];
  }

  if (pathname.startsWith("/connect/booking-history")) {
    return ["connect.booking_history.view"];
  }

  if (pathname.startsWith("/connect/non-project-im")) {
    return pathname.includes("/create")
      ? ["im.create"]
      : ["im.view"];
  }

  if (pathname.includes("/create")) {
    return [
      "connect.meetings.book",
      "connect.vehicles.book",
      "connect.visitors.create",
    ];
  }

  if (pathname === "/connect" || pathname === "/connect/") {
    return ["connect.dashboard.view"];
  }

  return ["connect.dashboard.view"];
}

export function canAccessConnectPath(
  pathname: string,
  context?: ConnectAccessContext,
): boolean {
  if (!pathname.startsWith("/connect")) {
    return true;
  }

  if (!canOpenConnect(context)) {
    return false;
  }

  const permissions = contextPermissions(context);
  const required = requiredPermissionsForPath(pathname);

  // hasAnyPermission already handles super_admin bypass via resolveRoles()
  return hasAnyPermission(permissions, required);
}

export function getConnectFallbackPath(context?: ConnectAccessContext): string {
  return canAccessConnectPath("/connect", context) ? "/connect" : "/tasks";
}
